
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ReferralLinkCard from '@/components/ReferralLinkCard';
import { useAuth } from '@/contexts/AuthContext';
import { Gift, ChevronRight, ArrowRight, HelpCircle } from 'lucide-react';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '@/components/ui/tooltip';

const ReferralProgram = () => {
  const { user, hasActiveSubscription } = useAuth();
  const navigate = useNavigate();
  
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow py-12 px-4 bg-gradient-to-br from-white via-mint/5 to-skyblue/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent mb-3">
              Comparte y Gana
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Invita a tus amigos a unirse a Guía Vegana App y gana dinero real por cada uno que se suscriba
            </p>
          </div>
          
          <div className="grid md:grid-cols-12 gap-6">
            <div className="md:col-span-7">
              <div className="bg-white p-6 rounded-xl shadow-md mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center text-white">
                    <Gift className="h-5 w-5" />
                  </div>
                  <h2 className="text-xl font-semibold">Programa de Referidos</h2>
                </div>
                <p className="mb-4 text-gray-600">
                  Por cada amigo que invites y se suscriba a Guía Vegana, obtendrás una comisión del 50% de su pago, y tu amigo tendrá el 30% de descuento en el primer mes. Si paga 35€, tu recibirás 17.5€, y el obtendrá el 30% de descuento. así de fácil! ¡Cuantos más amigos invites, más ganas!
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="h-6 w-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center font-bold text-sm">
                      1
                    </div>
                    <div>
                      <h3 className="font-medium">Comparte tu enlace</h3>
                      <p className="text-sm text-gray-500">Comparte tu enlace único con amigos y familiares</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="h-6 w-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center font-bold text-sm">
                      2
                    </div>
                    <div>
                      <h3 className="font-medium">Tus amigos se suscriben</h3>
                      <p className="text-sm text-gray-500">Cuando completan el registro y se suscriben,terminados los días 3 de prueba, se te asigna la comisión</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="h-6 w-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center font-bold text-sm">
                      3
                    </div>
                    <div>
                      <h3 className="font-medium">Recibe tus ganancias</h3>
                      <p className="text-sm text-gray-500">Las comisiones se acumulan en tu cuenta y puedes solicitar el pago desde el panel de Rewardful</p>
                    </div>
                  </div>
                </div>
                
                {/* Información de cómo iniciar sesión en Rewardful */}
                <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg mb-4">
                  <div className="flex items-start gap-3">
                    <div className="text-amber-600 mt-0.5">
                      <HelpCircle className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-amber-800">¿Cómo acceder al panel de Rewardful?</p>
                      <p className="text-xs text-amber-700 mt-1">
                        Registrate en la plataforma Rewardful con el botón aquí abajo para obtener tu enlace de referidos, ver tus estadisticas y retirar el dinero que has acumulado.
                      </p>
                    </div>
                  </div>
                </div>
                
                {!hasActiveSubscription() && (
                  <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="text-amber-600">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-amber-800">¡IMPORTANTE!</p>
                        <p className="text-xs text-amber-700 mt-1">
                          Para garantizar la calidad de nuestro programa de referidos, necesitarás tener una suscripción activa para recibir pagos por tus referidos.
                        </p>
                       
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 rounded-xl text-white flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-medium mb-1">¿Necesitas ayuda? Contacta con Soporte</h3>
                </div>
                <Button 
                  variant="secondary" 
                  className="whitespace-nowrap"
                  onClick={() => navigate('/soporte')}
                >
                  Ver ayuda <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
            
            <div className="md:col-span-5">
              <ReferralLinkCard />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ReferralProgram;
